import Button from "components/atoms/Button/Button";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import getLabel from "helpers/getLabel";
import { updateInvoice } from "api";
import CardNew from "components/molecules/Card/CardNew";
import Dropdown from "components/atoms/Dropdown/Dropdown";

function PaymentRouting({ invoice, isLoading, getAuditLog }) {
  const { permissions } = usePermissions();
  const paymentLocations = permissions && JSON.parse(permissions).payment_location.all_locations;
  const methods = useForm();
  const { handleSubmit, getValues, setValue, register } = methods;
  const intl = useIntl();

  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  const [auditOptions, setAuditOptions] = useState({});
  const [hasValueChanged, setHasValueChanged] = useState(false);

  useEffect(() => {
    const auditOptions = invoice?.audit_options;
    if (auditOptions && auditOptions.invoice_field1_enabled > 0) {
      setValue("invoice_field1", auditOptions.invoice_field1_initial_value);
    }
    setValue("location", auditOptions?.location);
    setAuditOptions(auditOptions);
  }, [invoice]);

  const handleSave = () => {
    setIsSaveInProgress(true);
    const payload = {
      location: getValues("location") || auditOptions.location,
      invoice_field1: getValues("invoice_field1") || auditOptions.invoice_field1_initial_value,
    };
    updateInvoice(invoice.invoice_id, payload)
      .then((res) => {
        if (res.status === 200) toast("Payment routing saved", { type: "success" });
        getAuditLog();
      })
      .catch((error) => errorToast(error))
      .finally(() => {
        setIsSaveInProgress(false);
        setHasValueChanged(false);
      });
  };

  if (!auditOptions?.can_be_audited) return null;
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleSave)}>
        <CardNew
          className="mb-15"
          title={<FormattedMessage id="billing.paymentRouting" defaultMessage="Payment routing" />}
          isLoading={isLoading}
        >
          <label className="mb-1 mt-1 font-b8 text-primary">{getLabel("Payment location", "capitalize")}</label>
          <Dropdown
            key={invoice?.invoice_id}
            options={paymentLocations}
            onChange={(item) => {
              setValue("location", item.value);
              setHasValueChanged(true);
            }}
            defaultValue={paymentLocations.find((l) => l.value === auditOptions?.location)?.label}
          />
          {auditOptions.invoice_field1_enabled > 0 && (
            <>
              <label className="mb-1 mt-2 font-b8 text-primary">{getLabel("Invoice field 01", "capitalize")}</label>
              <input
                {...register("invoice_field1")}
                type="text"
                required
                className="rounded-input"
                onChange={() => {
                  setHasValueChanged(true);
                }}
              />
            </>
          )}
          {hasValueChanged && (
            <Button
              className="rounded-pill ml-auto font-12 mt-2"
              style={{ padding: "6px 16px" }}
              label={
                isSaveInProgress ? (
                  <FontAwesomeIcon icon={faCircleNotch} className="font-16" spin />
                ) : (
                  intl.formatMessage({
                    id: "general.save",
                    defaultMessage: "Save",
                  })
                )
              }
              variant="primary"
              type="submit"
            />
          )}
        </CardNew>
      </form>
    </FormProvider>
  );
}

PaymentRouting.propTypes = {
  invoice: PropTypes.instanceOf(Object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  getAuditLog: PropTypes.func.isRequired,
};

export default PaymentRouting;
